import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useConfirm } from "@/context/ConfirmContext";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, X, Mail, Phone, Pencil, ShieldCheck } from "lucide-react";
import { PhoneInput } from "@/components/PhoneInput";

const SERVICE_OPTIONS = ["makeup", "photography", "mehendi", "videography", "hair", "assistant"];

function RoleBadge({ role }) {
  const isAdmin = role === "admin";
  return (
    <span data-testid="role-badge" className={`inline-block px-2 py-1 text-[10px] uppercase tracking-[0.25em] mt-1 ${isAdmin ? "text-[#829384]" : "text-[#C88270]"}`}>
      {isAdmin ? "Admin" : "Team Member"}
    </span>
  );
}

function UserModal({ open, mode, user, onClose, onSaved }) {
  const { refresh, user: me } = useAuth();
  const isSelf = mode === "edit" && me?.id === user?.id;
  const [form, setForm] = useState({ email: "", name: "", role: "team_member", phone: "", service_types: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && user) {
      setForm({ email: user.email || "", name: user.name || "", role: user.role || "team_member", phone: user.phone || "", service_types: user.service_types || [] });
    } else {
      setForm({ email: "", name: "", role: "team_member", phone: "", service_types: [] });
    }
  }, [open, mode, user]);

  if (!open) return null;

  const isTeamMember = form.role === "team_member";
  const toggleService = (s) => setForm({ ...form, service_types: form.service_types.includes(s) ? form.service_types.filter((x) => x !== s) : [...form.service_types, s] });

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        phone: isTeamMember ? form.phone : null,
        service_types: isTeamMember ? form.service_types : [],
      };
      if (mode === "edit") {
        await api.patch(`/users/${user.id}`, payload);
        toast.success("Details updated");
        if (me?.id === user.id) await refresh();
      } else {
        await api.post("/users", payload);
        toast.success("Team member added");
      }
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start md:items-center justify-center p-4 overflow-y-auto" data-testid="user-modal">
      <div className="bg-white border border-[#EAE7E0] max-w-lg w-full my-8">
        <div className="flex items-center justify-between p-6 border-b border-[#EAE7E0]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652]">{mode === "edit" ? "Edit" : "New"}</div>
            <h2 className="font-serif text-3xl tracking-tight">{mode === "edit" ? "Edit Member" : "Add Member"}</h2>
          </div>
          <button onClick={onClose} className="text-[#5C5652] hover:text-[#2A2421]" data-testid="close-team-modal"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Full name *</label>
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name-input"
              className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Google email *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="team-email-input"
                placeholder="name@gmail.com"
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none placeholder:text-[#B8B2AA] placeholder:normal-case placeholder:tracking-normal" />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="team-role-select"
                disabled={isSelf}
                className="w-full border-b border-[#EAE7E0] bg-transparent py-2 focus:border-[#C88270] focus:outline-none disabled:opacity-60">
                <option value="team_member">Team Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
          </div>
          {isTeamMember && (
            <>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-2">Phone</label>
                <PhoneInput value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="team-phone-input" variant="line" placeholder="90000 00000" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#5C5652] mb-3">Services</label>
                <div className="flex flex-wrap gap-2">
                  {SERVICE_OPTIONS.map((s) => (
                    <button type="button" key={s} onClick={() => toggleService(s)} data-testid={`service-${s}`}
                      className={`px-4 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${form.service_types.includes(s) ? "bg-[#C88270] text-white border-[#C88270]" : "border-[#EAE7E0] text-[#5C5652] hover:border-[#2A2421] hover:text-[#2A2421]"}`}>{s}</button>
                  ))}
                </div>
              </div>
            </>
          )}
          <div className="flex justify-end gap-3 pt-4 border-t border-[#EAE7E0]">
            <button type="button" onClick={onClose} className="px-6 py-2 text-xs uppercase tracking-[0.2em] text-[#5C5652]">Cancel</button>
            <button type="submit" disabled={saving} data-testid="team-submit-button"
              className="px-6 py-2 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors disabled:opacity-50">
              {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Add member"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function TeamPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState({ open: false, mode: "create", user: null });
  const confirm = useConfirm();

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/users"); setUsers(data); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!(await confirm({ title: "Remove team member?", description: "This permanently removes their account.", confirmText: "Remove" }))) return;
    try { await api.delete(`/users/${id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const members = users.filter((u) => u.role === "team_member");
  const admins = users.filter((u) => u.role === "admin");

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16" data-testid="team-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-3">Studio</div>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight text-[#2A2421]">Team</h1>
        </div>
        <button onClick={() => setModal({ open: true, mode: "create", user: null })} data-testid="add-team-member-button"
          className="inline-flex items-center gap-2 px-6 py-3 bg-[#C88270] text-white text-xs uppercase tracking-[0.25em] hover:bg-[#B37363] transition-colors">
          <Plus size={16} /> Add member
        </button>
      </div>

      {loading ? (
        <div className="text-xs uppercase tracking-[0.3em] text-[#5C5652]">Loading…</div>
      ) : (
        <>
          {/* Admin accounts */}
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-4 flex items-center gap-2"><ShieldCheck size={14} /> Administrators</div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {admins.map((a) => (
              <div key={a.id} className="p-6 bg-white border border-[#829384]/40" data-testid={`admin-user-${a.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="font-serif text-2xl tracking-tight text-[#2A2421]">{a.name}{me?.id === a.id && <span className="text-xs text-[#5C5652]"> (you)</span>}</div>
                    <RoleBadge role="admin" />
                  </div>
                  <button onClick={() => setModal({ open: true, mode: "edit", user: a })} className="text-[#5C5652] hover:text-[#C88270]" data-testid={`edit-user-${a.id}`}><Pencil size={16} /></button>
                </div>
                <div className="space-y-2 text-xs text-[#5C5652] border-t border-[#EAE7E0] pt-4">
                  <div className="flex items-center gap-2"><Mail size={14} strokeWidth={1.5} /> {a.email}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Team members */}
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#5C5652] mb-4">Team Members</div>
          {members.length === 0 ? (
            <div className="p-12 bg-white border border-[#EAE7E0] text-center text-[#5C5652]">No team members yet.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {members.map((m) => (
                <div key={m.id} className="p-6 bg-white border border-[#EAE7E0] group" data-testid={`team-member-${m.id}`}>
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="font-serif text-2xl tracking-tight text-[#2A2421]">{m.name}</div>
                      <RoleBadge role="team_member" />
                    </div>
                    <div className="flex items-center gap-3">
                      <button onClick={() => setModal({ open: true, mode: "edit", user: m })} className="text-[#5C5652] hover:text-[#C88270]" data-testid={`edit-user-${m.id}`}><Pencil size={16} /></button>
                      <button onClick={() => del(m.id)} className="text-[#5C5652] hover:text-red-700" data-testid={`delete-team-${m.id}`}><Trash2 size={16} /></button>
                    </div>
                  </div>
                  <div className="space-y-2 text-xs text-[#5C5652] border-t border-[#EAE7E0] pt-4">
                    <div className="flex items-center gap-2"><Mail size={14} strokeWidth={1.5} /> {m.email}</div>
                    {m.phone && <div className="flex items-center gap-2"><Phone size={14} strokeWidth={1.5} /> {m.phone}</div>}
                  </div>
                  {m.service_types?.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                      {m.service_types.map((s) => (
                        <span key={s} className="px-2 py-1 text-[10px] uppercase tracking-[0.2em] bg-[#FAF9F6] border border-[#EAE7E0] text-[#5C5652]">{s}</span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <UserModal open={modal.open} mode={modal.mode} user={modal.user} onClose={() => setModal({ open: false, mode: "create", user: null })} onSaved={load} />
    </div>
  );
}
